import { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EventNoteIcon from "@mui/icons-material/EventNote";
import DescriptionIcon from "@mui/icons-material/Description";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import FolderIcon from "@mui/icons-material/Folder";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { buildGotoUrl, GotoApp } from "../utils/chatisUrls.ts";

/**
 * 遷移先アプリへ引き継ぐダークモード状態を URL パラメータから読む。
 * ショートカット起動時の mode は Chatis の /goto が付与しており、それをそのまま渡す。
 */
const isDarkModeFromUrl = (): boolean =>
  new URLSearchParams(window.location.search).get("mode") === "dark";

const NAV_ITEMS = [
  { key: "note", label: "ノート", Icon: DescriptionIcon },
  { key: "calendar", label: "カレンダー", Icon: EventNoteIcon },
  { key: "todo", label: "TODO", Icon: PlaylistAddCheckIcon },
  { key: "files", label: "ファイル", Icon: FolderIcon },
] as const;

interface ShortcutHeaderProps {
  honbuId: number;
  roomId: number;
  color: PaletteColor;
}

export function ShortcutHeader({
  honbuId,
  roomId,
  color,
}: ShortcutHeaderProps) {
  const [navigating, setNavigating] = useState(false);

  const handleNavigate = async (key: GotoApp) => {
    if (navigating || key === "todo") return;
    setNavigating(true);
    try {
      // Step 1: 遷移前チェック（ルームメンバーシップ / isgroup / BOT確認 / hpcode取得）
      const checkRes = await fetch(
        `/api/Shortcut/nav-check?honbuId=${honbuId}&roomId=${roomId}&target=${key}`,
      );
      if (!checkRes.ok) {
        setNavigating(false);
        return;
      }
      const checkData: { allowed: boolean; hpcode: string | null } =
        await checkRes.json();
      if (!checkData.allowed) {
        setNavigating(false);
        return;
      }

      // Step 2: Chatis の /goto へ遷移する。
      // 遷移先アプリも temp token を必要とするため、Chatis 側でログイン確認と
      // トークン発行を済ませてもらう（TODO 側でプロキシ発行はしない）。
      window.location.href = buildGotoUrl({
        app: key,
        roomId,
        color,
        isDarkMode: isDarkModeFromUrl(),
        extra: checkData.hpcode ? { hpcode: checkData.hpcode } : undefined,
      });
    } catch {
      // エラー時は遷移しない
    } finally {
      setNavigating(false);
    }
  };

  const handleHelp = () => {
    window.postMessage({ type: "openHelp" }, "*");
  };

  return (
    <div
      style={{
        backgroundColor: PaletteColorHex[color],
        backgroundImage:
          "linear-gradient(to bottom, rgba(255,255,255,0.12), rgba(255,255,255,0))",
        display: "flex",
        alignItems: "center",
        padding: "0 16px",
        height: "48px",
        color: "white",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontWeight: "bold",
          fontSize: "16px",
          letterSpacing: "0.05em",
        }}
      >
        TODO（ショートカット）
      </span>
      <div style={{ flex: 1 }} />
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        {NAV_ITEMS.map(({ key, label, Icon }) => {
          const isActive = key === "todo";
          return (
            <Tooltip key={key} title={label}>
              <span>
                <IconButton
                  size="small"
                  disabled={navigating}
                  onClick={() => handleNavigate(key)}
                  sx={{
                    color: "inherit",
                    backgroundColor: isActive
                      ? "rgba(255,255,255,0.3)"
                      : "transparent",
                    borderRadius: "4px",
                    padding: "4px",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          );
        })}
        <div
          style={{
            width: "1px",
            height: "20px",
            backgroundColor: "rgba(255,255,255,0.5)",
            marginLeft: "4px",
          }}
        />
        <Tooltip title="ヘルプ">
          <IconButton
            size="small"
            onClick={handleHelp}
            sx={{
              color: "inherit",
              borderRadius: "4px",
              padding: "4px",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
            }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}
